import React, { useRef, useMemo } from "react";
import { motion, useInView } from "framer-motion";
import { Award, Users, Globe, Heart, MessageCircle } from "lucide-react";

const WHATSAPP_LINK = `https://wa.me/971541873527?text=Hello%20Guru%20Maa,%20I%20would%20like%20to%20schedule%20a%20personalized%20astrology%20consultation.%20Please%20guide%20me.`;

// Generate random stars for the galaxy background
const generateStars = (count) => {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    size: Math.random() * 2.5 + 0.5,
    delay: Math.random() * 5,
    duration: Math.random() * 3 + 2,
    opacity: Math.random() * 0.5 + 0.3,
  }));
};

// Galaxy Background with drifting stars and nebula
const GalaxyBackground = () => {
  const stars = useMemo(() => generateStars(80), []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Nebula gradients */}
      <div
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse at 20% 30%, rgba(139, 92, 246, 0.15) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 70%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
            radial-gradient(ellipse at 50% 50%, rgba(59, 130, 246, 0.08) 0%, transparent 60%)
          `,
        }}
      />

      {/* Drifting stars */}
      {stars.map((star) => (
        <motion.div
          key={star.id}
          className="absolute rounded-full bg-white"
          style={{
            left: star.left,
            top: star.top,
            width: `${star.size}px`,
            height: `${star.size}px`,
            opacity: star.opacity,
          }}
          animate={{
            opacity: [star.opacity * 0.5, star.opacity, star.opacity * 0.5],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: star.duration,
            repeat: Infinity,
            delay: star.delay,
            ease: "easeInOut",
          }}
        />
      ))}

      {/* Light particles drifting slowly */}
      <motion.div
        className="absolute w-1 h-1 rounded-full bg-[#D4AF37]/60"
        style={{ left: "10%", top: "20%" }}
        animate={{ y: [0, 30, 0], x: [0, 10, 0], opacity: [0.3, 0.7, 0.3] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute w-1.5 h-1.5 rounded-full bg-[#8B5CF6]/50"
        style={{ left: "85%", top: "60%" }}
        animate={{ y: [0, -25, 0], x: [0, -15, 0], opacity: [0.2, 0.6, 0.2] }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2,
        }}
      />
      <motion.div
        className="absolute w-0.5 h-0.5 rounded-full bg-white/70"
        style={{ left: "70%", top: "30%" }}
        animate={{ y: [0, 20, 0], opacity: [0.4, 0.8, 0.4] }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
      />
    </div>
  );
};

// Large Planet component with 3D-like glow and depth
const Planet = ({
  size,
  color,
  glowColor,
  orbitRadius,
  duration,
  delay,
  zIndex,
  hasRing = false,
  isMobile = false,
}) => {
  // Skip outer orbit planets on mobile for performance
  if (isMobile && orbitRadius > 180) return null;

  return (
    <div
      className="absolute"
      style={{
        width: orbitRadius * 2,
        height: orbitRadius * 2,
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
      }}
    >
      {/* Orbital Path - more visible glowing ring */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          border: "1.5px solid rgba(212, 175, 55, 0.25)",
          boxShadow: `
            inset 0 0 40px rgba(212, 175, 55, 0.08),
            0 0 25px rgba(212, 175, 55, 0.1),
            0 0 8px rgba(212, 175, 55, 0.15)
          `,
        }}
      />

      {/* Planet with light trail effect */}
      <motion.div
        className="absolute"
        style={{
          width: size,
          height: size,
          left: "50%",
          top: 0,
          marginLeft: -size / 2,
          marginTop: -size / 2,
          zIndex: zIndex,
        }}
        animate={{ rotate: 360 }}
        transition={{
          duration: duration,
          repeat: Infinity,
          ease: "linear",
          delay: delay,
        }}
      >
        {/* Light trail */}
        <div
          className="absolute rounded-full"
          style={{
            width: size * 3,
            height: size * 0.6,
            background: `linear-gradient(90deg, transparent, ${glowColor}50, ${glowColor}30, transparent)`,
            left: -size,
            top: size * 0.2,
            filter: "blur(6px)",
            transform: "rotate(-25deg)",
            opacity: 0.8,
          }}
        />

        {/* Planet body with vivid 3D gradient */}
        <div
          className="relative rounded-full"
          style={{
            width: size,
            height: size,
            background: `
              radial-gradient(circle at 25% 25%, white 0%, ${color} 20%, ${glowColor} 70%, ${color} 100%)
            `,
            boxShadow: `
              0 0 ${size}px ${glowColor},
              0 0 ${size * 1.5}px ${glowColor}80,
              0 0 ${size * 2}px ${glowColor}40,
              inset -${size / 5}px -${size / 5}px ${size / 2}px rgba(0,0,0,0.5),
              inset ${size / 6}px ${size / 6}px ${size / 3}px rgba(255,255,255,0.4)
            `,
          }}
        >
          {/* Ring for Saturn-like planets */}
          {hasRing && (
            <div
              className="absolute"
              style={{
                width: size * 2,
                height: size * 0.5,
                left: -size * 0.5,
                top: size * 0.25,
                border: `3px solid ${glowColor}70`,
                borderRadius: "50%",
                transform: "rotateX(70deg)",
                boxShadow: `0 0 10px ${glowColor}50`,
              }}
            />
          )}
        </div>
      </motion.div>
    </div>
  );
};

// Celestial composition with Guru Maa and orbiting planets
const CelestialComposition = ({ isMobile }) => {
  const planets = [
    // Inner orbit - faster planets (small, bright)
    {
      size: 20,
      color: "#FFD700",
      glowColor: "#FF8C00",
      orbitRadius: 100,
      duration: 18,
      delay: 0,
      zIndex: 25,
    },
    {
      size: 14,
      color: "#E0E0E0",
      glowColor: "#B0B0B0",
      orbitRadius: 100,
      duration: 18,
      delay: 9,
      zIndex: 8,
    },

    // Second orbit - medium speed, vivid colors
    {
      size: 32,
      color: "#FF4444",
      glowColor: "#FF6B6B",
      orbitRadius: 145,
      duration: 28,
      delay: 4,
      zIndex: 30,
    },
    {
      size: 22,
      color: "#00CED1",
      glowColor: "#20B2AA",
      orbitRadius: 145,
      duration: 28,
      delay: 18,
      zIndex: 12,
    },

    // Third orbit - slower, larger planets with rich colors
    {
      size: 42,
      color: "#FFD700",
      glowColor: "#DAA520",
      orbitRadius: 195,
      duration: 42,
      delay: 0,
      zIndex: 35,
      hasRing: true,
    },
    {
      size: 28,
      color: "#9370DB",
      glowColor: "#8A2BE2",
      orbitRadius: 195,
      duration: 42,
      delay: 21,
      zIndex: 15,
    },

    // Outer orbit - slowest, majestic planets
    {
      size: 36,
      color: "#FF8C00",
      glowColor: "#FF6347",
      orbitRadius: 250,
      duration: 58,
      delay: 12,
      zIndex: 32,
    },
    {
      size: 26,
      color: "#FF69B4",
      glowColor: "#FF1493",
      orbitRadius: 250,
      duration: 58,
      delay: 38,
      zIndex: 18,
    },
  ];

  return (
    <div className="celestial-container relative flex items-center justify-center">
      {/* Galaxy Background */}
      <GalaxyBackground />

      {/* Outer Ethereal Glow */}
      <div
        className="absolute rounded-full"
        style={{
          width: "140%",
          height: "140%",
          background:
            "radial-gradient(circle, rgba(212, 175, 55, 0.12) 0%, rgba(139, 92, 246, 0.06) 40%, transparent 70%)",
          filter: "blur(60px)",
          animation: "pulse-glow 6s ease-in-out infinite",
        }}
      />

      {/* Orbital Paths and Planets */}
      {planets.map((planet, index) => (
        <Planet key={index} {...planet} isMobile={isMobile} />
      ))}

      {/* Central Guru Maa Image */}
      <div className="relative z-40">
        {/* Multi-layer glow effect */}
        <div
          className="absolute rounded-full"
          style={{
            width: "180%",
            height: "180%",
            left: "-40%",
            top: "-40%",
            background:
              "radial-gradient(circle, rgba(212, 175, 55, 0.2) 0%, transparent 60%)",
            filter: "blur(30px)",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: "140%",
            height: "140%",
            left: "-20%",
            top: "-20%",
            background:
              "radial-gradient(circle, rgba(212, 175, 55, 0.3) 0%, transparent 50%)",
            filter: "blur(15px)",
          }}
        />

        {/* Guru Maa Image */}
        <img
          src="../../../images/kali-mata.jpeg"
          alt="Guru Maa Pooja Sharma"
          className="guru-maa-about-image relative z-10"
          data-testid="about-guru-maa-image"
        />
      </div>

      {/* Floating cosmic elements */}
      <motion.div
        animate={{ opacity: [0.3, 0.8, 0.3], scale: [1, 1.3, 1] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        className="absolute z-50"
        style={{ top: "5%", right: "10%" }}
      >
        <span
          className="text-2xl"
          style={{ textShadow: "0 0 15px rgba(212, 175, 55, 0.9)" }}
        >
          ✦
        </span>
      </motion.div>
      <motion.div
        animate={{ opacity: [0.4, 0.9, 0.4], scale: [1, 1.2, 1] }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1.5,
        }}
        className="absolute z-50"
        style={{ bottom: "10%", left: "5%" }}
      >
        <span
          className="text-xl"
          style={{ textShadow: "0 0 12px rgba(139, 92, 246, 0.9)" }}
        >
          ✧
        </span>
      </motion.div>
      <motion.div
        animate={{ opacity: [0.2, 0.7, 0.2], y: [-5, 5, -5] }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 3,
        }}
        className="absolute z-50"
        style={{ top: "70%", right: "5%" }}
      >
        <span
          className="text-lg"
          style={{ textShadow: "0 0 10px rgba(252, 211, 77, 0.8)" }}
        >
          ⭐
        </span>
      </motion.div>
    </div>
  );
};

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  // Detect mobile for simplified animations
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  const highlights = [
    { icon: Award, label: "15+ Years Experience" },
    { icon: Users, label: "10,000+ Happy Clients" },
    { icon: Globe, label: "Global Consultations" },
    { icon: Heart, label: "Blessed by Divine Grace" },
  ];

  return (
    <section
      id="about"
      className="relative py-20 md:py-32 overflow-hidden"
      data-testid="about-section"
      ref={ref}
    >
      {/* Background cosmic gradient */}
      <div
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse at 30% 20%, rgba(139, 92, 246, 0.08) 0%, transparent 50%),
            radial-gradient(ellipse at 70% 80%, rgba(212, 175, 55, 0.06) 0%, transparent 50%)
          `,
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Celestial Composition Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative flex items-center justify-center order-2 lg:order-1"
          >
            <CelestialComposition isMobile={isMobile} />
          </motion.div>

          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="order-1 lg:order-2"
          >
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest font-medium">
              About Our Expert
            </span>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mt-4 mb-6">
              Guru Maa <span className="text-gold-gradient">Pooja Sharma</span>
            </h2>

            <div className="space-y-4 text-[#94A3B8]">
              <p className="text-base md:text-lg leading-relaxed">
                With over 15 years of dedicated practice in Vedic Astrology,
                Guru Maa Pooja Sharma has guided thousands of individuals
                towards their true destiny. Her profound understanding of
                celestial energies and ancient wisdom has transformed countless
                lives across the globe.
              </p>

              <p className="text-base md:text-lg leading-relaxed">
                Blessed with the divine gift of foresight, Guru Maa combines
                traditional Vedic knowledge with intuitive spiritual guidance to
                provide accurate predictions and life-changing solutions for
                love, career, health, and prosperity.
              </p>

              <p className="text-[#D4AF37] italic text-lg mt-6">
                "Every challenge has a celestial solution waiting to be
                discovered."
              </p>
            </div>

            {/* Highlights Grid */}
            <div className="grid grid-cols-2 gap-4 mt-8">
              {highlights.map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                  className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/5"
                >
                  <item.icon
                    className="text-[#D4AF37] flex-shrink-0"
                    size={24}
                  />
                  <span className="text-sm text-[#F8FAFC]">{item.label}</span>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.8 }}
              className="mt-10"
            >
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 whatsapp-btn px-8 py-4 rounded-full text-lg font-bold"
                data-testid="about-whatsapp-btn"
              >
                <MessageCircle size={22} />
                <span>Connect with Guru Maa</span>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
